
import { computed, defineComponent, nextTick, onMounted, PropType, reactive, ref } from 'vue'
import { ethers, providers } from 'ethers'
import erc20ABI from '@/contract/abi/erc20'
import redPackABI from '@/contract/abi/HappyRedPacket.json'
import BN from 'bignumber.js'
import { RedPacketContract } from '@/contract/func/redPacket'
import { TokenContract } from '@/contract/func/token'
import { useRoute } from 'vue-router'
import GreetingCard from './GreetingCard.vue'
import ChainTag from './ChainTag.vue'
import PersonalityButton from './common/PersonalityButton.vue'
import FxInput from './common/FxInput.vue'
import AccountConnect from './accountConnect.vue'
import { getChainId } from '@/composables/metamask'
import { IChainId } from '@/interface'
import { ITokenInfoRes } from '@/composables/fxwallet'
import { Tools } from '@/plugins/tools'
import { useI18n } from 'vue-i18n'
import Loading from './common/loading.vue'
import config from '@/config'
import { generateThemeTitle } from '@/composables/redpack'
import { useStore } from 'vuex'
import { Chain } from '@/contract/rpc'
import { WalletEnable } from '@/contract/wallet'

export interface IRedPackData {
  totalToken: string,
  number: string,
  title: string
}

export default defineComponent({
  components: { GreetingCard, ChainTag, PersonalityButton, FxInput, AccountConnect, Loading },

  props: {
    chainId: {
      type: Number as PropType<Chain>,
      required: true
    },
    address: {
      type: String,
      required: true
    },
    redPackContractAddr: {
      type: String,
      required: true
    },
    provider: {
      type: Object,
      required: true
    },
    tokenInfo: {
      type: Object as PropType<ITokenInfoRes>,
      required: true
    },
    sendLoading: {
      type: Boolean
    },
    balance: {
      type: String,
      required: true
    }
  },

  emits: ['sendRedPack'],

  expose: ['allowance', 'checkAllowance'],

  setup (props, { emit }) {
    const { t } = useI18n()
    const route = useRoute()
    const query = route.query as { token: string }
    const store = useStore()

    const defaultTitle = generateThemeTitle()

    const loading = reactive({
      approve: false,
      allowance: true
    })

    let tokenContract: TokenContract

    // 红包信息
    const redPackData = reactive<IRedPackData>({
      totalToken: '',
      number: '5',
      title: ''
    })

    const wallet = computed(() => {
      return store.state.wallet.wallet as WalletEnable
    })

    const contractErc20 = computed(() => {
      return store.state.contractErc20.contract as TokenContract
    })

    const redPackNumberChangeType = computed(() => {
      return Number(redPackData.number)
    })

    const redPackDataTotalTokenDecimal = computed(() => {
      return Tools.amountPlusDecimal(redPackData.totalToken || '0', props.tokenInfo.decimals)
    })

    const approveHash = ref('')
    const approveStatus = ref('')
    const approveAllowance = computed(() => {
      return store.state.contractErc20.allowance
    })
    const approveAllowanceLoading = ref(false)
    const sendStatus = ref(true)
    const redPackId = ref('')

    const stepStatus = ref(false)

    const maxStatus = computed(() => {
      return new BN(Tools.amountPlusDecimal(redPackData.totalToken, props.tokenInfo.decimals)).eq(props.balance)
    })

    const redPackNumberRule = {
      min: 1,
      max: 255
    }

    /**
     * 是否允许发起交易
     * 配额大于输入的金额
     */
    const isAllowSend = computed(() => {
      return new BN(approveAllowance.value).gte(props.balance)
    })

    const isCanSendTx = computed(() => {
      return isAllowSend.value
    })

    function approveTxData () {
      return contractErc20.value.encode('approve', [
        props.redPackContractAddr,
        '0x' + 'f'.repeat(64)
      ])
    }

    /**
     * 授权
     */
    async function approve () {
      try {
        loading.approve = true

        const txParams: providers.TransactionRequest = {
          to: contractErc20.value.contract.address,
          data: approveTxData()
        }

        console.log(txParams)

        const tx = await wallet.value.signer.sendTransaction(txParams)

        await wallet.value.provider.waitForTransaction(tx.hash)

        await allowance()

        if (allowanceEnough()) {
          sendStatus.value = false
        }
      } catch (error) {
        console.log(error)
      }

      loading.approve = false
    }

    function allowanceEnough () {
      console.log(store.state.contractErc20.allowance, props.balance)

      return new BN(store.state.contractErc20.allowance).gte(props.balance)
    }

    /**
     * 查询配额
     */
    async function allowance () {
      approveAllowanceLoading.value = true

      await store.dispatch('contractErc20/allowance', {
        owner: props.address,
        spender: props.redPackContractAddr
      })

      approveAllowanceLoading.value = false

      loading.allowance = false
    }

    /**
     * 创建红包
     */
    function createRedPack () {
      emit('sendRedPack', {
        number: redPackData.number,
        title: config.skinImage + (redPackData.title || defaultTitle),
        tokenContract: props.tokenInfo.contract,
        tokenType: props.tokenInfo.contract ? 1 : 0,
        totalToken: redPackData.totalToken
      })
    }

    function redPackNumberBlur () {
      if (redPackNumberChangeType.value > redPackNumberRule.max) {
        redPackData.number = redPackNumberRule.max.toString()
      }

      if (redPackNumberChangeType.value < redPackNumberRule.min) {
        redPackData.number = redPackNumberRule.min.toString()
      }
    }

    function redPackNumberHandler (method: 'plus' | 'minu') {
      if (method === 'minu' && redPackNumberChangeType.value > redPackNumberRule.min) {
        redPackData.number = (redPackNumberChangeType.value - 1).toString()
      }

      if (method === 'plus' && redPackNumberChangeType.value < redPackNumberRule.max) {
        redPackData.number = (redPackNumberChangeType.value + 1).toString()
      }
    }

    /**
     * 填充全部金额
     */
    function setMaxAmount () {
      redPackData.totalToken = Tools.amountDivDecimal(props.balance, props.tokenInfo.decimals)

      totalTokenBlurHandler()
    }

    /**
     * 校验
     */
    const numberRegexp = new RegExp(`^[0-9]*(\\.[0-9]{0,${config.decimal}})?$`)
    let totalTokenOldVal = ''
    const numberOldVal = 0
    let titleOldVal = ''
    const validatorStatus = reactive({
      totalToken: {
        msg: '',
        isValid: true
      },
      number: {
        msg: '',
        isValid: true
      },
      title: {
        msg: '',
        isValid: true
      }
    })

    const canISend = computed(() => {
      return validatorStatus.totalToken.isValid && validatorStatus.number.isValid && validatorStatus.title.isValid && redPackData.totalToken && redPackData.number
    })

    async function totalTokenInputHandler () {
      totalTokenOldVal = redPackData.totalToken

      await nextTick(() => {
        if (!numberRegexp.test(redPackData.totalToken as string)) {
          redPackData.totalToken = totalTokenOldVal
        }
      })
    }

    async function totalTokenBlurHandler () {
      if (new BN(Tools.amountPlusDecimal(redPackData.totalToken, props.tokenInfo.decimals)).gt(props.balance)) {
        validatorStatus.totalToken.isValid = false
        validatorStatus.totalToken.msg = t('v1.0.form.ruleError.InsufficientBalance')
      } else if (new BN(redPackData.totalToken).lt('0.0001')) {
        validatorStatus.totalToken.isValid = false
        validatorStatus.totalToken.msg = t('v1.0.form.ruleError.limitMinAmount', { amount: '0.0001' })
      } else {
        validatorStatus.totalToken.isValid = true
      }
    }

    async function titleInputHandler () {
      titleOldVal = redPackData.title

      await nextTick(() => {
        console.log(titleOldVal)
        if (Tools.byteLength(redPackData.title) > 32) {
          redPackData.title = titleOldVal
        }
      })
    }

    function checkAllowance () {
      if (!allowanceEnough()) {
        stepStatus.value = true
      }
    }

    onMounted(async () => {
      if (props.tokenInfo.contract) {
      //   tokenContract = new TokenContract(
      //     props.provider.getSigner(),
      //     props.tokenInfo.contract
      //   )

        //   // loading.allowance = true

        // await allowance()

        //   console.log('approveAllowance', approveAllowance.value)

        // checkAllowance()
      }
    })

    return {
      approveHash,
      approveStatus,
      approveAllowance,
      approve,
      allowance,
      isAllowSend,
      isCanSendTx,
      stepStatus,
      createRedPack,
      redPackData,
      redPackNumberRule,
      redPackNumberChangeType,
      redPackNumberBlur,
      redPackNumberHandler,
      redPackId,
      loading,
      sendStatus,
      setMaxAmount,
      maxStatus,
      totalTokenInputHandler,
      totalTokenBlurHandler,
      // redPackNumberInputHandler,
      titleInputHandler,
      validatorStatus,
      canISend,
      defaultTitle,
      checkAllowance
    }
  }
})
